// src/contexts/ModalContext.js
import { createContext, useState, useCallback, useEffect } from 'react';
import { logEvent } from '../services/loggingService';
import { LogEventType } from '../utils/data/enums';

export const ModalTypes = {
    DEFAULT: 'DEFAULT',
    CONFIRMATION: 'CONFIRMATION',
    ACTION: 'ACTION',
    DELETE_CONFIRMATION: 'DELETE_CONFIRMATION',
    PROFILE: 'PROFILE',
    SECURITY: 'PASSWORD',
    COMMUNICATIONS: 'COMMUNICATIONS',
    FAQ: 'FAQ',
    PRIVACY: 'PRIVACY',
    SUPPORT: 'SUPPORT',
    INSTALLATION: 'INSTALLATION',
    ADD_FROM_ASSETS: 'ADD_FROM_ASSETS',
    UPLOAD_FROM_DEVICE: 'UPLOAD_FROM_DEVICE',
    ASSET_VIEW: 'ASSET_VIEW',
    ADDITIONAL_USER_FORM: 'ADDITIONAL_USER_FORM',
    MESSAGE_VIEW: 'MESSAGE_VIEW',
    IMPORT_MAP_TEST: 'IMPORT_MAP_TEST',
    ADD_SERVICE: 'ADD_SERVICE',
};

/**
 * Modal configuration object.
 * @typedef {Object} ModalConfig
 * @property {string} [title] - The title of the modal.
 * @property {string} [subtitle] - The subtitle of the modal.
 * @property {string} [text] - The main text content of the modal.
 * @property {ModalTypes} [type] - The type of modal to display.
 * @property {Function} [onYes] - Callback when confirming action.
 * @property {Function} [onNo] - Callback when cancelling action.
 * @property {string} [yesButtonText] - Text for the 'Yes' button.
 * @property {string} [noButtonText] - Text for the 'No' button.
 * @property {string} [userId] - User ID for additional user form modal.
 * @property {Function} [onSuccess] - Callback when form action succeeds.
 */

/**
 * Modal context with default values.
 * @type {React.Context}
 */
export const ModalContext = createContext({
    showModal: config => {},
    hideModal: () => {},
    modalStack: [],
});

/**
 * Provides modal context to children components.
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {React.ReactNode} The provider component.
 */
export const ModalProvider = ({ children }) => {
    const [modalStack, setModalStack] = useState([]);

    const showModal = useCallback(config => {
        const modalConfig = {
            ...config,
            yesButtonText: config.yesButtonText || 'Yes',
            noButtonText: config.noButtonText || 'No',
            type: config.type || ModalTypes.DEFAULT,
            id: Date.now() + Math.random(), // Unique ID for each modal
        };

        setModalStack(prevStack => [...prevStack, modalConfig]);
    }, []);

    const hideModal = useCallback(() => {
        setModalStack(prevStack => {
            if (prevStack.length === 0) return prevStack;

            const topModal = prevStack[prevStack.length - 1];
            if (!window.location.pathname.startsWith('/view-live')) {
                logEvent(
                    LogEventType.CancelClicked,
                    `Closed modal '${topModal.type}'`,
                );
            }

            return prevStack.slice(0, -1);
        });
    }, []);

    const hideAllModals = useCallback(() => {
        setModalStack([]);
    }, []);

    // Escape key handler to close top modal
    useEffect(() => {
        const handleKeyDown = event => {
            if (
                (event.key === 'Escape' || event.key === 'Esc') &&
                modalStack.length > 0
            ) {
                hideModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalStack, hideModal]);

    return (
        <ModalContext.Provider
            value={{ showModal, hideModal, hideAllModals, modalStack }}
        >
            {children}
        </ModalContext.Provider>
    );
};
